import { useQuery } from '@tanstack/react-query';
import { Clima, ClimaService } from '../service/clima';
import { useEffect, useState } from 'react';
import { useGeolocationPlace } from './useGeolocationPlace';
import { useStates } from './useStates';
import { useCities } from './useCities';

const service = new ClimaService();

const DEFAULT_STATE_CODE = 43;
const DEFAULT_CITY_CODE = 4316006;
const DEFAULT_COORDINATES = {
  lat: -29.6462,
  lon: -50.5819,
};

export function useWeatherForecast() {
  const [selectedStateCode, setSelectedStateCode] =
    useState<number>(DEFAULT_STATE_CODE);
  const [selectedCity, setSelectedCity] = useState<number>(DEFAULT_CITY_CODE);
  const [persistedWeatherData, setPersistedWeatherData] = useState<Clima | null>(null);
  const [coordinates, setCoordinates] = useState<{ lat: number; lon: number }>(
    DEFAULT_COORDINATES,
  );

  const { lat, lon } = coordinates;

  const { locationData } = useGeolocationPlace(
    coordinates.lat,
    coordinates.lon,
  );

  const { stateData, isLoading: isStatesLoading } = useStates();
  const { cityData, isLoading: isCitiesLoading } = useCities(selectedStateCode);

  const {
    data: weatherData,
    isLoading,
    isFetching,
    isRefetching,
  } = useQuery({
    queryKey: ['weatherForecast', lat, lon],
    queryFn: () => {
      if (lat && lon) {
        return service
          .getWeatherForecast({ lat, lon })
          .then((res) => res.dados || null);
      }
      return Promise.resolve(null);
    },
    enabled: !!lat && !!lon,
    staleTime: 1000 * 60 * 60 * 3, // 3 horas de cache
  });

  useEffect(() => {
    if (cityData.length > 0) {
      const cidadeValida = cityData.find((c) => c.ib === selectedCity);
      if (!cidadeValida) {
        setSelectedCity(cityData[0].ib);
      }
    }
  }, [selectedStateCode, cityData]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          setCoordinates({ lat, lon });
        },
        () => {
          setCoordinates(DEFAULT_COORDINATES);
        },
      );
    }
  }, []);

  useEffect(() => {
    if (!locationData) return;

    const { estado, municipio } = locationData;

    const estadoEncontrado = stateData.find((uf) => uf.nome === estado);
    if (!estadoEncontrado) return;

    setSelectedStateCode(estadoEncontrado.codigo_uf);

    const cidadeEncontrada = cityData.find((city) => city.n === municipio);
    if (cidadeEncontrada) {
      setSelectedCity(cidadeEncontrada.ib);
    }
  }, [locationData]);

  useEffect(() => {
    if (weatherData) {
      setPersistedWeatherData(weatherData);
    }
  }, [weatherData]);

  useEffect(() => {
    const selectedCityObj = cityData.find((city) => city.ib === selectedCity);

    if (selectedCityObj && selectedCityObj.la && selectedCityObj.lo) {
      setCoordinates({
        lat: selectedCityObj.la,
        lon: selectedCityObj.lo,
      });
    }
  }, [selectedCity]);

  return {
    weatherData: weatherData || null,
    isLoading: isLoading,
    isFetching,
    isRefetching,
    selectedCity,
    cityData,
    stateData,
    selectedStateCode,
    setSelectedStateCode,
    isStatesLoading,
    setSelectedCity,
    isCitiesLoading,
    persistedWeatherData,
  };
}
